/**
 * `SettingsIntegrations` — provider-cards layout for the per-site
 * connector surface (ADR-0054 §2).
 *
 * One Card per provider: Shopify, Stripe, WooCommerce,
 * Custom embed (available — no credential to disconnect). Each Card
 * surfaces status pill + key fields + Connect or Disconnect affordance.
 * Disconnect uses the inline two-state coral confirm pattern from
 * `SettingsEndpoint:362-427` (the rotate-key precedent).
 *
 * The page fetches each connected provider's state via two parallel
 * `GET /v1/sites/:site_id/connector?kind=<...>` calls (per ADR-0054
 * §3.3 — no aggregate endpoint). Refetches on the URL `connected`
 * flag so the post-Connect redirect from onboarding / sites-list lands
 * back at this page with the new state visible.
 *
 * Security: the Disconnect CTAs are hidden for `reviewer|viewer` roles
 * (the server is authoritative — owner|admin enforced server-side; UI
 * gating is defense-in-depth).
 */

import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Cloud,
  CreditCard,
  Download,
  ExternalLink,
  Package,
  RefreshCw,
  Send,
  ShoppingBag,
  X,
  Zap,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { WORDPRESS_PLUGIN_VERSION, type Site, type SiteId } from '@sill/types';
import type {
  CloudflareIntegrationStatus,
  ConnectorStatus,
  ConnectorStatusKindFilter,
  InstallUpdateItem,
  SiteAPI,
  SlackWebhookStatus,
} from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { planFeatureRequiredDetails } from '../../api/plan-feature.js';
import { useAuth } from '../../app/auth-context.js';
import { API_ORIGIN } from '../../app/env.js';
import { useSites, useToast } from '../../app/providers.js';
import {
  CLOUDFLARE_ROUTE_IN_USE_REASON,
  cloudflareRouteInUseMessage,
} from '../../lib/cloudflare-route-in-use.js';
import { useC } from '../../lib/theme.js';
import {
  buildShopifyInstallUrl,
  deriveShopFromDomain,
  isValidShopInput,
} from '../../lib/shopify-install.js';
import { Card, Pill, RelativeTime } from '../../components/atoms/index.js';
import { InstallCoverageCard } from '../sites/InstallCoverageCard.js';
import {
  WooCommerceConnectForm,
  WooCommerceInstructions,
} from '../shared/WooCommerceConnect.js';
import { InstallKeysSection } from './InstallKeysSection.js';
import { confirmKeyRotation } from './install-key-rotation.js';
import { SettlementRailSection } from './SettlementRailSection.js';
import { SiteSettlementRailSection } from './SiteSettlementRailSection.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface SettingsIntegrationsProps {
  site: Site;
}

/** Kinds the page fetches connector-status for on mount. ADR-0170 §2
 *  wires WooCommerce as a real manual-key connector; custom_embed has
 *  no credential and never surfaces here. */
const FETCHED_KINDS: readonly ConnectorStatusKindFilter[] = [
  'shopify',
  'stripe',
  'woocommerce',
];

export function SettingsIntegrations({
  site,
}: SettingsIntegrationsProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const toast = useToast();
  const auth = useAuth();

  // Connector statuses keyed by kind. `undefined` → not yet fetched;
  // `null` → fetch errored out (the Card surfaces a generic error pill).
  const [shopify, setShopify] = useState<ConnectorStatus | null | undefined>(
    undefined,
  );
  const [stripe, setStripe] = useState<ConnectorStatus | null | undefined>(
    undefined,
  );
  const [woocommerce, setWoocommerce] = useState<
    ConnectorStatus | null | undefined
  >(undefined);
  // ADR-0098 v2 — Slack webhook status. Separate from the ConnectorStatus
  // pattern because Slack has its own endpoint (no integration row).
  const [slackStatus, setSlackStatus] = useState<
    SlackWebhookStatus | null | undefined
  >(undefined);
  // ADR-0170 §2 — the POST /connector/woocommerce response echoes the
  // canonicalized store URL, but the GET status endpoint does not
  // surface it. We stash the just-connected URL here so the merchant
  // sees the exact host Sill will call immediately after connect;
  // remounts fall back to the pill-only view fed by the status fetch.
  const [wooConnectedUrl, setWooConnectedUrl] = useState<string | null>(null);
  // Inline confirm state — at most one provider's confirm is open at a
  // time so the page stays focused on the merchant's current intent.
  const [confirming, setConfirming] = useState<
    ConnectorStatusKindFilter | null
  >(null);
  const [disconnecting, setDisconnecting] = useState<
    ConnectorStatusKindFilter | null
  >(null);

  // Post-OAuth scroll-to + flash on the Stripe card.
  const stripeCardRef = useRef<HTMLDivElement>(null);
  const [stripeFlash, setStripeFlash] = useState(false);

  // Slack-specific mutation state (separate from the ConnectorStatus disconnect flow).
  const [slackConfirming, setSlackConfirming] = useState(false);
  const [slackSaving, setSlackSaving] = useState(false);
  const [slackDeleting, setSlackDeleting] = useState(false);
  const [slackTesting, setSlackTesting] = useState(false);
  const [slackToggling, setSlackToggling] = useState(false);

  const role =
    auth.state.kind === 'authenticated' ? auth.state.user.role : null;
  const canDisconnect = role === 'owner' || role === 'admin';

  const refresh = useCallback(async (): Promise<void> => {
    // Parallel fetches — two allSettled calls run concurrently so the
    // connector-status results retain their per-index types (homogeneous
    // array) while the Slack status (different shape) is separate.
    const [[shopify_res, stripe_res, woocommerce_res], [slack_res]] =
      await Promise.all([
        Promise.allSettled(
          FETCHED_KINDS.map((k) => api.sites.getConnectorStatus(site.site_id, k)),
        ),
        Promise.allSettled([api.sites.getSlackWebhookStatus(site.site_id)]),
      ]);
    if (shopify_res !== undefined && shopify_res.status === 'fulfilled') {
      setShopify(shopify_res.value);
    } else if (shopify_res !== undefined) {
      setShopify(null);
    }
    if (stripe_res !== undefined && stripe_res.status === 'fulfilled') {
      setStripe(stripe_res.value);
    } else if (stripe_res !== undefined) {
      setStripe(null);
    }
    if (woocommerce_res !== undefined && woocommerce_res.status === 'fulfilled') {
      setWoocommerce(woocommerce_res.value);
    } else if (woocommerce_res !== undefined) {
      setWoocommerce(null);
    }
    if (slack_res !== undefined && slack_res.status === 'fulfilled') {
      setSlackStatus(slack_res.value);
    } else if (slack_res !== undefined) {
      setSlackStatus(null);
    }
  }, [api, site.site_id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Refetch when the merchant returns from an OAuth round-trip. The
  // `connected=<kind>` URL flag triggers an immediate refetch so the
  // post-OAuth state is visible without waiting for the next poll cycle.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.has('connected')) {
      void refresh();
    }
  }, [refresh]);

  // Surface the server-side connect-error params the OAuth callbacks
  // emit on a redirect-back (currently `role_insufficient`: the member
  // who began the install was demoted / suspended / deactivated inside
  // the 10-minute state TTL and the callback refused to persist the
  // credential). Fires once on mount, then strips the one-shot flag so
  // a re-render doesn't re-toast on the next paint.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const err = params.get('connect_error');
    if (err !== 'role_insufficient') return;
    toast({
      title: 'Could not finish connecting',
      body:
        'Your access changed while this connection was in progress, so it '
        + 'was not saved. Ask an owner or admin to connect again.',
      tone: 'coral',
    });
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('connect_error');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    } catch {
      /* private-mode / sandbox iframe — degrade silently */
    }
  }, [toast]);

  // Scroll to + flash-highlight the Stripe card on a post-OAuth return.
  // Runs once on mount; the 350ms delay lets the refetch above settle and
  // React paint the connected state before scrolling.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') !== 'stripe') return;
    // One-shot flag: drop it so it doesn't ride along on copied links.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('connected');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch {
      /* private-mode / sandbox iframe — degrade silently */
    }
    const t = setTimeout(() => {
      stripeCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setStripeFlash(true);
      const t2 = setTimeout(() => setStripeFlash(false), 2200);
      return () => clearTimeout(t2);
    }, 350);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDisconnect = useCallback(
    async (kind: ConnectorStatusKindFilter): Promise<void> => {
      if (disconnecting !== null) return;
      setDisconnecting(kind);
      try {
        await api.sites.disconnectConnector(site.site_id, kind);
        toast({
          title: disconnectTitleFor(kind),
          body: disconnectBodyFor(kind),
          tone: 'lime',
        });
        setConfirming(null);
        // ADR-0170 §2 — a WooCommerce disconnect clears the just-
        // connected URL so the card returns to the not-connected form
        // without holding onto a now-stale store URL.
        if (kind === 'woocommerce') setWooConnectedUrl(null);
        await refresh();
      } catch (err) {
        const reason = err instanceof ApiError
          ? toastReasonForApiError(err)
          : 'Unknown error';
        toast({
          title: disconnectErrorTitleFor(kind),
          body: reason,
          tone: 'coral',
        });
      } finally {
        setDisconnecting(null);
      }
    },
    [api, disconnecting, refresh, site.site_id, toast],
  );

  const handleSlackSave = useCallback(
    async (url: string): Promise<void> => {
      if (slackSaving) return;
      setSlackSaving(true);
      try {
        await api.sites.saveSlackWebhook(site.site_id, { url });
        toast({ title: 'Slack connected', body: 'Sill alerts will be posted to your channel.', tone: 'lime' });
        await refresh();
      } catch (err) {
        const reason = err instanceof ApiError ? toastReasonForApiError(err) : 'Unknown error';
        toast({ title: 'Could not connect Slack', body: reason, tone: 'coral' });
      } finally {
        setSlackSaving(false);
      }
    },
    [api, refresh, site.site_id, slackSaving, toast],
  );

  const handleSlackDelete = useCallback(async (): Promise<void> => {
    if (slackDeleting) return;
    setSlackDeleting(true);
    try {
      await api.sites.deleteSlackWebhook(site.site_id);
      toast({ title: 'Slack disconnected', body: 'Sill alerts will no longer be posted to Slack.', tone: 'lime' });
      setSlackConfirming(false);
      await refresh();
    } catch (err) {
      const reason = err instanceof ApiError ? toastReasonForApiError(err) : 'Unknown error';
      toast({ title: 'Could not disconnect Slack', body: reason, tone: 'coral' });
    } finally {
      setSlackDeleting(false);
    }
  }, [api, refresh, site.site_id, slackDeleting, toast]);

  const handleSlackTest = useCallback(async (): Promise<void> => {
    if (slackTesting) return;
    setSlackTesting(true);
    try {
      await api.sites.testSlackWebhook(site.site_id);
      toast({ title: 'Test message sent', body: 'Check your Slack channel — a grey test card should arrive shortly.', tone: 'lime' });
    } catch (err) {
      const reason = err instanceof ApiError ? toastReasonForApiError(err) : 'Unknown error';
      toast({ title: 'Test message failed', body: reason, tone: 'coral' });
    } finally {
      setSlackTesting(false);
    }
  }, [api, site.site_id, slackTesting, toast]);

  const [slackSampleAlertSending, setSlackSampleAlertSending] = useState(false);
  const handleSlackSendSampleAlert = useCallback(async (): Promise<void> => {
    if (slackSampleAlertSending) return;
    setSlackSampleAlertSending(true);
    try {
      await api.sites.sendSampleSlackSecurityAlert(site.site_id);
      toast({
        title: 'Sample alert sent',
        body: 'Check your Slack channel — a sample got-through alert should arrive shortly. The data is fictional.',
        tone: 'lime',
      });
    } catch (err) {
      const reason = err instanceof ApiError ? toastReasonForApiError(err) : 'Unknown error';
      toast({ title: 'Sample alert failed', body: reason, tone: 'coral' });
    } finally {
      setSlackSampleAlertSending(false);
    }
  }, [api, site.site_id, slackSampleAlertSending, toast]);

  const handleSlackToggle = useCallback(async (enabled: boolean): Promise<void> => {
    if (slackToggling) return;
    setSlackToggling(true);
    try {
      await api.sites.toggleSlackWebhook(site.site_id, enabled);
      setSlackStatus((prev) =>
        prev !== null && prev !== undefined && prev.configured
          ? { ...prev, enabled }
          : prev,
      );
    } catch (err) {
      const reason = err instanceof ApiError ? toastReasonForApiError(err) : 'Unknown error';
      toast({ title: 'Could not update Slack notifications', body: reason, tone: 'coral' });
    } finally {
      setSlackToggling(false);
    }
  }, [api, site.site_id, slackToggling, toast]);

  return (
    <div className="space-y-5">
      <Card className="p-5 sm:p-6">
        <div
          className="mb-5 pb-4"
          style={{ borderBottom: `1px solid ${C.border}` }}
        >
          <div
            style={{
              fontFamily: serifFont,
              fontSize: 22,
              color: C.text,
              letterSpacing: -0.5,
              marginBottom: 4,
            }}
          >
            Integrations
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              lineHeight: 1.5,
            }}
          >
            What's connected to this site. Disconnect to revoke Sill's
            access; reconnect any time.
          </div>
        </div>

        <ShopifyCard
          C={C}
          site={site}
          status={shopify}
          canDisconnect={canDisconnect}
          confirming={confirming === 'shopify'}
          onConfirm={() => setConfirming('shopify')}
          onCancel={() => setConfirming(null)}
          onDisconnect={() => void handleDisconnect('shopify')}
          disconnecting={disconnecting === 'shopify'}
        />
        <div ref={stripeCardRef}>
          <StripeCard
            C={C}
            site={site}
            status={stripe}
            canDisconnect={canDisconnect}
            canEditRail={canDisconnect}
            confirming={confirming === 'stripe'}
            onConfirm={() => setConfirming('stripe')}
            onCancel={() => setConfirming(null)}
            onDisconnect={() => void handleDisconnect('stripe')}
            disconnecting={disconnecting === 'stripe'}
            highlighted={stripeFlash}
            // ADR-0038 §13 — after the rail-toggle PUT resolves, patch the
            // in-page connector-status snapshot so the pill flips without
            // a round-trip. The server stays the source of truth; a next
            // refresh will refetch the row verbatim.
            onRailToggled={(enabled) => {
              setStripe((prev) => (prev === null || prev === undefined ? prev : { ...prev, stripe_rail_enabled: enabled }));
            }}
          />
        </div>
        <WooCommerceCard
          C={C}
          site={site}
          status={woocommerce}
          canDisconnect={canDisconnect}
          confirming={confirming === 'woocommerce'}
          onConfirm={() => setConfirming('woocommerce')}
          onCancel={() => setConfirming(null)}
          onDisconnect={() => void handleDisconnect('woocommerce')}
          disconnecting={disconnecting === 'woocommerce'}
          connectedUrl={wooConnectedUrl}
          onConnected={(url) => {
            setWooConnectedUrl(url);
            void refresh();
          }}
        />
        <SlackCard
          C={C}
          site={site}
          status={slackStatus}
          canEdit={canDisconnect}
          confirming={slackConfirming}
          saving={slackSaving}
          deleting={slackDeleting}
          testing={slackTesting}
          toggling={slackToggling}
          sampleAlertSending={slackSampleAlertSending}
          onSave={handleSlackSave}
          onConfirmDelete={() => setSlackConfirming(true)}
          onCancelDelete={() => setSlackConfirming(false)}
          onDelete={() => void handleSlackDelete()}
          onTest={() => void handleSlackTest()}
          onSendSampleAlert={() => void handleSlackSendSampleAlert()}
          onToggleEnabled={(enabled) => void handleSlackToggle(enabled)}
        />
        {/* ADR follow-up (2026-10-01) — the "Custom embed" row carried
            no credential and no CTA (EMBED-ONLY pill with no content).
            Integrations now only lists real connections (Shopify,
            Stripe, WooCommerce, Slack, settlement rail). The
            Cloudflare Worker + plugin download controls moved to
            Settings → Install (same batch, 2026-10-01). */}
      </Card>

      {/* ADR-0081 — per-account settlement-rail preference. Account-level
          control, surfaced here because the connected rails the merchant
          chooses among are exactly the cards above. Owner/admin only;
          renders nothing for reviewer/viewer. */}
      <SettlementRailSection />

      {/* ADR-0096 — per-SITE rail override. Below the account default so the
          relationship reads top-down: account default, then this site's
          override. Owner/admin only. */}
      <SiteSettlementRailSection site_id={site.site_id} />

      {/* ADR-0099 Slice 2b — per-SITE auto-refund moved to the
          Guardrails view (2026-10-01). The auto-refund rail is a
          guardrail-shaped relax of the HITL refund checkpoint, which
          sits closer to policy rules than to connector plumbing. */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Provider cards
// ---------------------------------------------------------------------------

export type ThemeC = ReturnType<typeof useC>;

interface ProviderCardProps {
  C: ThemeC;
  site: Site;
  status: ConnectorStatus | null | undefined;
  canDisconnect: boolean;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onDisconnect: () => void;
  disconnecting: boolean;
}

function ShopifyCard({
  C,
  site,
  status,
  canDisconnect,
  confirming,
  onConfirm,
  onCancel,
  onDisconnect,
  disconnecting,
}: ProviderCardProps): JSX.Element {
  // Connect/Reconnect routes server-side are owner/admin only (same
  // role as disconnect). Reuse the gate — hide the Connect affordance
  // for reviewer/viewer and surface a short read-only note instead.
  const canManage = canDisconnect;
  // Derive the shop from the site domain when possible; for custom-domain
  // sites the merchant must enter their underlying *.myshopify.com.
  // Both the not-connected and reconnect surfaces share the same gate
  // — neither may emit an install URL with a missing or invalid shop
  // (the API 400s `invalid_shop` otherwise).
  const derivedShop = useMemo(
    () => deriveShopFromDomain(site.domain),
    [site.domain],
  );
  const [shopInput, setShopInput] = useState<string>('');
  const trimmedShopInput = shopInput.trim();
  const shopInputValid =
    trimmedShopInput.length > 0 && isValidShopInput(trimmedShopInput);
  const effectiveShop = derivedShop ?? (shopInputValid ? trimmedShopInput : null);
  const installUrl =
    effectiveShop !== null
      ? buildShopifyInstallUrl(API_ORIGIN, site.site_id, effectiveShop)
      : null;
  const needsShopInput = derivedShop === null;
  const state = readState(status, 'shopify');

  return (
    <Section C={C} title="Shopify" icon={ShoppingBag}>
      {state === 'loading' ? (
        <StatusRow C={C} pill={<Pill tone="neutral">LOADING</Pill>} />
      ) : state === 'not_connected' ? (
        <>
          <StatusRow
            C={C}
            pill={<Pill tone="neutral">NOT CONNECTED</Pill>}
            body="Connect your Shopify store to expose live catalog and order data to verified agents."
          />
          {canManage ? (
            <>
              {needsShopInput ? (
                <ShopifyShopInput
                  C={C}
                  site={site}
                  value={shopInput}
                  onChange={setShopInput}
                  invalid={trimmedShopInput.length > 0 && !shopInputValid}
                />
              ) : null}
              <ConnectAction
                C={C}
                href={installUrl ?? '#'}
                label="Connect Shopify"
                disabled={installUrl === null}
              />
            </>
          ) : (
            <ReadOnlyConnectHint C={C} />
          )}
        </>
      ) : state === 'error' ? (
        <>
          <StatusRow
            C={C}
            pill={<Pill tone="amber" icon={AlertTriangle}>ERROR</Pill>}
            body="The connection is in an error state. Reconnect to restore access."
          />
          {canManage ? (
            <>
              {needsShopInput ? (
                <ShopifyShopInput
                  C={C}
                  site={site}
                  value={shopInput}
                  onChange={setShopInput}
                  invalid={trimmedShopInput.length > 0 && !shopInputValid}
                />
              ) : null}
              <ConnectAction
                C={C}
                href={installUrl ?? '#'}
                label="Reconnect Shopify"
                disabled={installUrl === null}
              />
            </>
          ) : (
            <ReadOnlyConnectHint C={C} />
          )}
        </>
      ) : (
        <ShopifyConnectedDetails
          C={C}
          status={status as ConnectorStatus}
          canDisconnect={canDisconnect}
          confirming={confirming}
          onConfirm={onConfirm}
          onCancel={onCancel}
          onDisconnect={onDisconnect}
          disconnecting={disconnecting}
        />
      )}
    </Section>
  );
}

function ShopifyShopInput({
  C,
  site,
  value,
  onChange,
  invalid,
}: {
  C: ThemeC;
  site: Site;
  value: string;
  onChange: (v: string) => void;
  invalid: boolean;
}): JSX.Element {
  return (
    <div>
      <label
        htmlFor="settings-shopify-shop-input"
        style={{
          display: 'block',
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 6,
        }}
      >
        YOUR SHOPIFY STORE DOMAIN
      </label>
      <input
        id="settings-shopify-shop-input"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="your-store.myshopify.com"
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        aria-invalid={invalid}
        style={{
          width: '100%',
          maxWidth: 360,
          padding: '8px 10px',
          background: C.surface2,
          border: `1px solid ${invalid ? C.coralFg : C.border}`,
          borderRadius: 2,
          color: C.text,
          fontFamily: monoFont,
          fontSize: 12,
          outline: 'none',
        }}
      />
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.5,
          marginTop: 6,
        }}
      >
        {site.domain} is on a custom domain. Enter the underlying
        Shopify tenant (it ends in .myshopify.com) so we can install
        the app on the right store.
      </div>
    </div>
  );
}

function ShopifyConnectedDetails({
  C,
  status,
  canDisconnect,
  confirming,
  onConfirm,
  onCancel,
  onDisconnect,
  disconnecting,
}: {
  C: ThemeC;
  status: ConnectorStatus;
  canDisconnect: boolean;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onDisconnect: () => void;
  disconnecting: boolean;
}): JSX.Element {
  const embed_label = formatShopifyEmbedLabel(status.embed_install_status);
  return (
    <>
      <StatusRow C={C} pill={<Pill tone="mint" icon={Check}>CONNECTED</Pill>} />
      <DetailGrid C={C}>
        {status.shop_domain !== undefined ? (
          <Detail C={C} label="Shop">
            <span style={{ fontFamily: monoFont, fontSize: 12 }}>
              {status.shop_domain}
            </span>
          </Detail>
        ) : null}
        {status.wired_skills.length > 0 ? (
          <Detail C={C} label="Skills">
            <span style={{ fontFamily: monoFont, fontSize: 12 }}>
              {status.wired_skills.join(' · ')}
            </span>
          </Detail>
        ) : null}
        {embed_label !== null ? (
          <Detail C={C} label="Embed">
            <span style={{ fontFamily: sansFont, fontSize: 12 }}>
              {embed_label}
            </span>
          </Detail>
        ) : null}
        {status.scopes !== undefined && status.scopes.length > 0 ? (
          <Detail C={C} label="Scopes">
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                lineHeight: 1.4,
                wordBreak: 'break-word',
              }}
            >
              {status.scopes.join(', ')}
            </span>
          </Detail>
        ) : null}
        {status.last_sync_at !== undefined ? (
          <Detail C={C} label="Last sync">
            <RelativeTime
              iso={status.last_sync_at}
              style={{ fontFamily: sansFont, fontSize: 12 }}
            />
          </Detail>
        ) : null}
      </DetailGrid>
      {canDisconnect ? (
        <DisconnectArea
          C={C}
          confirming={confirming}
          disconnecting={disconnecting}
          onConfirm={onConfirm}
          onCancel={onCancel}
          onDisconnect={onDisconnect}
          confirmTitle="Disconnect Shopify"
          confirmBody={
            <>
              Disconnecting revokes Sill's access to your Shopify store and
              stops dispatching commerce skills. Discovery (agent visit
              identification) keeps running on this site.{' '}
              <strong>
                To fully remove the embed, uninstall the Sill app in your
                Shopify admin afterward.
              </strong>
            </>
          }
        />
      ) : (
        <ReadOnlyHint C={C} />
      )}
    </>
  );
}

interface StripeCardProps extends ProviderCardProps {
  highlighted?: boolean;
  // ADR-0038 §13 — the rail-enabled toggle is owner/admin only. The
  // server enforces the same role gate as defense-in-depth; the flag
  // here lets the toggle render disabled for reviewer/viewer so the
  // control is visible but not actionable.
  canEditRail?: boolean;
  // Called with the new enabled state after a successful PUT so the
  // parent can patch its ConnectorStatus snapshot without a refetch.
  onRailToggled?: (enabled: boolean) => void;
}

function StripeCard({
  C,
  site,
  status,
  canDisconnect,
  canEditRail,
  confirming,
  onConfirm,
  onCancel,
  onDisconnect,
  disconnecting,
  highlighted,
  onRailToggled,
}: StripeCardProps): JSX.Element {
  const installUrl = `${API_ORIGIN}/v1/oauth/stripe/install?site_id=${encodeURIComponent(site.site_id)}`;
  const state = readState(status, 'stripe');

  return (
    <>
      {highlighted && (
        <style>{`
          @keyframes sill-connect-flash {
            0%   { box-shadow: 0 0 0 2px #D9FF3F; background: rgba(217,255,63,0.10); border-radius: 4px; }
            55%  { box-shadow: 0 0 0 2px rgba(217,255,63,0.4); background: rgba(217,255,63,0.04); }
            100% { box-shadow: none; background: transparent; }
          }
          .sill-stripe-flash { animation: sill-connect-flash 2.2s ease-out forwards; }
        `}</style>
      )}
      <div className={highlighted ? 'sill-stripe-flash' : undefined}>
    <Section C={C} title="Stripe" icon={CreditCard}>
      {state === 'loading' ? (
        <StatusRow C={C} pill={<Pill tone="neutral">LOADING</Pill>} />
      ) : state === 'not_connected' ? (
        <>
          <StatusRow
            C={C}
            pill={<Pill tone="neutral">NOT CONNECTED</Pill>}
            body="Connect Stripe to authorize payment for verified place_order mandates."
          />
          {canDisconnect ? (
            <ConnectAction
              C={C}
              href={installUrl}
              label="Connect Stripe"
              disabled={false}
            />
          ) : (
            <ReadOnlyConnectHint C={C} />
          )}
        </>
      ) : state === 'error' ? (
        <>
          <StatusRow
            C={C}
            pill={<Pill tone="amber" icon={AlertTriangle}>ERROR</Pill>}
            body="The connection is in an error state. Reconnect to restore access."
          />
          {canDisconnect ? (
            <ConnectAction
              C={C}
              href={installUrl}
              label="Reconnect Stripe"
              disabled={false}
            />
          ) : (
            <ReadOnlyConnectHint C={C} />
          )}
        </>
      ) : (
        <StripeConnectedDetails
          C={C}
          site={site}
          status={status as ConnectorStatus}
          canDisconnect={canDisconnect}
          canEditRail={canEditRail === true}
          confirming={confirming}
          onConfirm={onConfirm}
          onCancel={onCancel}
          onDisconnect={onDisconnect}
          disconnecting={disconnecting}
          {...(onRailToggled !== undefined ? { onRailToggled } : {})}
        />
      )}
    </Section>
      </div>
    </>
  );
}

function StripeConnectedDetails({
  C,
  site,
  status,
  canDisconnect,
  canEditRail,
  confirming,
  onConfirm,
  onCancel,
  onDisconnect,
  disconnecting,
  onRailToggled,
}: {
  C: ThemeC;
  site: Site;
  status: ConnectorStatus;
  canDisconnect: boolean;
  canEditRail: boolean;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onDisconnect: () => void;
  disconnecting: boolean;
  onRailToggled?: (enabled: boolean) => void;
}): JSX.Element {
  const api = useApi();
  const toast = useToast();
  const railOn = status.stripe_rail_enabled === true;
  const [railSaving, setRailSaving] = useState(false);
  // ADR-0082 §2 — a read-only TEST / LIVE badge derived from the
  // connected account's `stripe_mode` (the connection's `livemode`).
  // A merchant must always be able to tell at a glance whether real
  // money can move. LIVE reads in the affirmative `mint` tone; TEST in
  // the muted `amber` tone. It is an indicator, never a toggle —
  // going live is a gated re-connect (ADR-0082 §3), not a switch here.
  const liveMode = status.stripe_mode === 'live';
  // ADR-0038 §13 — flip the rail from the integrations card. The
  // ConnectStripeSection carried a toggle but was only rendered during
  // onboarding, which left the disconnect dialog's "re-enable the rail"
  // guidance (and UcpCheckoutRow's "Turn it on under Integrations"
  // reason copy) pointing at a control the merchant could not see.
  // Owner/admin only — server enforces the same role gate 403
  // `role_insufficient`.
  const toggleRail = async (): Promise<void> => {
    if (!canEditRail || railSaving) return;
    const next = !railOn;
    setRailSaving(true);
    try {
      const res = await api.sites.setStripeRail(site.site_id, { enabled: next });
      if (onRailToggled) onRailToggled(res.stripe_rail_enabled);
      toast({
        title: res.stripe_rail_enabled ? 'Stripe rail turned on' : 'Stripe rail turned off',
        body: res.stripe_rail_enabled
          ? 'Approved mandates can now settle through Stripe.'
          : 'New charges are refused. Approved mandates still produce audit records.',
        tone: 'mint',
      });
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? err.server_reason
          : 'unknown';
      toast({
        title: 'Could not change the Stripe rail',
        body:
          reason === 'role_insufficient'
            ? 'Your role cannot change payment configuration. Ask an owner or admin.'
            : reason === 'stripe_not_connected'
              ? 'Reconnect Stripe before turning the rail on.'
              : reason === 'site_not_transactional_ready'
                ? 'Finish site verification to enable payments. This site has not reached the transactional tier yet.'
                : reason === 'rail_enable_live_mode_forbidden'
                  ? 'Live payments are not enabled for your account yet.'
                  : 'Try again shortly.',
        tone: 'coral',
      });
    } finally {
      setRailSaving(false);
    }
  };
  // Founder 2026-10-21 — honest "why am I still in Discovery?"
  // explainer for the sill.so case: an active Stripe credential (the
  // card is CONNECTED) but the site hasn't moved to
  // `transactional_active`. The ADR-0043 §2.6 OAuth domain-match is
  // the T2 gate; it evaluates false when the Stripe account's
  // `business_profile.url` doesn't match the site's registered domain
  // and no matching entry sits in `allowed_subdomains`. The fix on
  // the merchant side is a one-liner (set the business URL, then
  // reconnect), but
  // without this line the merchant sees "CONNECTED" + "Discovery
  // mode" and has no visible explanation. Owner/admin only — the
  // detail is a diagnostic, not a role-sensitive secret, but the
  // card is already behind the Settings → Integrations role gate.
  const stillInDiscovery = site.status === 'discovery_active';
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <Pill tone="mint" icon={Check}>CONNECTED</Pill>
        {status.stripe_mode !== undefined ? (
          <Pill tone={liveMode ? 'mint' : 'amber'}>
            {liveMode ? 'LIVE' : 'TEST'}
          </Pill>
        ) : null}
        <Pill tone={railOn ? 'mint' : 'neutral'}>
          {railOn ? 'RAIL ENABLED' : 'RAIL DISABLED'}
        </Pill>
      </div>
      {stillInDiscovery ? (
        <div
          data-testid="stripe-domain-match-not-met"
          className="mt-3 p-3"
          style={{ background: C.amberDim, border: `1px solid ${C.amberFg}`, borderRadius: 2 }}
        >
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
              lineHeight: 1.5,
            }}
          >
            <strong>Not taking orders yet.</strong>{' '}
            Set the business website in your Stripe account to{' '}
            <span style={{ fontFamily: monoFont }}>https://{site.domain}</span>,
            then reconnect Stripe.
          </div>
        </div>
      ) : null}
      <DetailGrid C={C}>
        {status.stripe_account_display_name !== undefined ? (
          <Detail C={C} label="Account">
            <span style={{ fontFamily: sansFont, fontSize: 12 }}>
              {status.stripe_account_display_name}
            </span>
          </Detail>
        ) : null}
        {status.stripe_account_id !== undefined ? (
          <Detail C={C} label="Account ID">
            <span
              style={{ fontFamily: monoFont, fontSize: 11.5, wordBreak: 'break-all' }}
              data-testid="stripe-account-id"
            >
              {status.stripe_account_id}
            </span>
          </Detail>
        ) : null}
        {status.stripe_mode !== undefined ? (
          <Detail C={C} label="Mode">
            <span style={{ fontFamily: sansFont, fontSize: 12 }}>
              {status.stripe_mode === 'test' ? 'Test mode' : 'Live mode'}
            </span>
          </Detail>
        ) : null}
        <Detail C={C} label="Rail">
          <span style={{ fontFamily: sansFont, fontSize: 12 }}>
            {railOn ? 'Enabled' : 'Disabled'}
          </span>
        </Detail>
        {status.last_sync_at !== undefined ? (
          <Detail C={C} label="Last sync">
            <RelativeTime
              iso={status.last_sync_at}
              style={{ fontFamily: sansFont, fontSize: 12 }}
            />
          </Detail>
        ) : null}
      </DetailGrid>
      {/* ADR-0038 §13 — Rail on/off control. Owner/admin only; reviewer
          and viewer see the current state but cannot change it (server
          enforces the same gate). The disconnect dialog below promises
          the merchant can "re-enable the rail" — this is the control
          that promise points at. */}
      <div
        className="mt-3 p-3 flex flex-wrap items-center gap-3"
        style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
      >
        <div className="flex-1 min-w-[200px]">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1.5,
              marginBottom: 2,
            }}
          >
            PAYMENT RAIL
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
            {railOn
              ? 'On — approved mandates settle to Stripe.'
              : 'Off — Sill signs the audit record but does not charge the card.'}
          </div>
        </div>
        {canEditRail ? (
          <button
            type="button"
            onClick={() => void toggleRail()}
            disabled={railSaving}
            data-testid="stripe-rail-toggle"
            aria-pressed={railOn}
            className="px-3 py-1.5"
            style={{
              background: railOn ? 'transparent' : C.lime,
              color: railOn ? C.text : C.ink,
              fontFamily: sansFont,
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 2,
              border: railOn ? `1px solid ${C.border}` : 'none',
              cursor: railSaving ? 'not-allowed' : 'pointer',
              opacity: railSaving ? 0.6 : 1,
            }}
          >
            {railSaving ? 'Saving…' : railOn ? 'Turn rail off' : 'Turn rail on'}
          </button>
        ) : (
          <span
            data-testid="stripe-rail-readonly"
            style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}
          >
            Owner or admin can change this.
          </span>
        )}
      </div>
      {canDisconnect ? (
        <DisconnectArea
          C={C}
          confirming={confirming}
          disconnecting={disconnecting}
          onConfirm={onConfirm}
          onCancel={onCancel}
          onDisconnect={onDisconnect}
          confirmTitle="Disconnect Stripe"
          confirmBody={
            <>
              Disconnecting revokes Sill's Stripe access and disables the
              payment rail. Pending mandates against this credential will
              fail.{' '}
              <strong>
                You'll need to reconnect Stripe and re-enable the rail to
                authorize payments again.
              </strong>
            </>
          }
        />
      ) : (
        <ReadOnlyHint C={C} />
      )}
    </>
  );
}

interface WooCommerceCardProps extends ProviderCardProps {
  /** URL echoed by the POST /connector/woocommerce success response —
   *  known ONLY right after connect. The GET status endpoint does not
   *  surface `site_url` for WooCommerce, so remounts fall back to the
   *  pill-only view; the merchant can still see the exact host
   *  Sill will call in the immediate post-connect state. */
  connectedUrl: string | null;
  /** Called with the canonicalized store URL from the POST response so
   *  the parent can stash it in `wooConnectedUrl` and refresh the
   *  status fetch (which flips the pill to CONNECTED). */
  onConnected: (site_url: string) => void;
}

function WooCommerceCard({
  C,
  site,
  status,
  canDisconnect,
  confirming,
  onConfirm,
  onCancel,
  onDisconnect,
  disconnecting,
  connectedUrl,
  onConnected,
}: WooCommerceCardProps): JSX.Element {
  const state = readState(status, 'woocommerce');
  return (
    <Section C={C} title="WooCommerce" icon={Package}>
      {state === 'loading' ? (
        <StatusRow C={C} pill={<Pill tone="neutral">LOADING</Pill>} />
      ) : state === 'connected' ? (
        <WooCommerceConnectedDetails
          C={C}
          status={status as ConnectorStatus}
          connectedUrl={connectedUrl}
          canDisconnect={canDisconnect}
          confirming={confirming}
          onConfirm={onConfirm}
          onCancel={onCancel}
          onDisconnect={onDisconnect}
          disconnecting={disconnecting}
        />
      ) : state === 'error' ? (
        <>
          <StatusRow
            C={C}
            pill={<Pill tone="amber" icon={AlertTriangle}>ERROR</Pill>}
            body="The connection is in an error state. Reconnect with a fresh REST API key to restore access."
          />
          {canDisconnect ? (
            <WooCommerceConnectForm
              C={C}
              site={site}
              onConnected={onConnected}
              idPrefix="settings-woo"
            />
          ) : (
            <ReadOnlyConnectHint C={C} />
          )}
        </>
      ) : (
        <>
          <StatusRow
            C={C}
            pill={<Pill tone="neutral">NOT CONNECTED</Pill>}
            body="Connect your WooCommerce store so verified agents can browse the live catalog and check availability."
          />
          {canDisconnect ? (
            <>
              <WooCommerceInstructions C={C} />
              <WooCommerceConnectForm
                C={C}
                site={site}
                onConnected={onConnected}
                idPrefix="settings-woo"
              />
            </>
          ) : (
            <ReadOnlyConnectHint C={C} />
          )}
        </>
      )}
    </Section>
  );
}

function WooCommerceConnectedDetails({
  C,
  status,
  connectedUrl,
  canDisconnect,
  confirming,
  onConfirm,
  onCancel,
  onDisconnect,
  disconnecting,
}: {
  C: ThemeC;
  status: ConnectorStatus;
  connectedUrl: string | null;
  canDisconnect: boolean;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onDisconnect: () => void;
  disconnecting: boolean;
}): JSX.Element {
  const showDetails = connectedUrl !== null || status.wired_skills.length > 0;
  return (
    <>
      <StatusRow C={C} pill={<Pill tone="mint" icon={Check}>CONNECTED</Pill>} />
      {showDetails ? (
        <DetailGrid C={C}>
          {connectedUrl !== null ? (
            <Detail C={C} label="Store URL">
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  wordBreak: 'break-all',
                }}
              >
                {connectedUrl}
              </span>
            </Detail>
          ) : null}
          {status.wired_skills.length > 0 ? (
            <Detail C={C} label="Skills">
              <span style={{ fontFamily: monoFont, fontSize: 12 }}>
                {status.wired_skills.join(' · ')}
              </span>
            </Detail>
          ) : null}
        </DetailGrid>
      ) : null}
      {canDisconnect ? (
        <DisconnectArea
          C={C}
          confirming={confirming}
          disconnecting={disconnecting}
          onConfirm={onConfirm}
          onCancel={onCancel}
          onDisconnect={onDisconnect}
          confirmTitle="Disconnect WooCommerce"
          confirmBody={
            <>
              Disconnecting revokes Sill's access to your WooCommerce store
              and stops dispatching commerce skills. Discovery (agent visit
              identification) keeps running on this site.{' '}
              <strong>
                To fully rescind access, delete the Sill REST API key from
                WooCommerce → Settings → Advanced → REST API afterward.
              </strong>
            </>
          }
        />
      ) : (
        <ReadOnlyHint C={C} />
      )}
    </>
  );
}

interface SlackCardProps {
  C: ThemeC;
  site: Site;
  status: SlackWebhookStatus | null | undefined;
  canEdit: boolean;
  confirming: boolean;
  saving: boolean;
  deleting: boolean;
  testing: boolean;
  toggling: boolean;
  sampleAlertSending: boolean;
  onSave: (url: string) => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
  onDelete: () => void;
  onTest: () => void;
  onSendSampleAlert: () => void;
  onToggleEnabled: (enabled: boolean) => void;
}

function SlackCard({
  C,
  status,
  canEdit,
  confirming,
  saving,
  deleting,
  testing,
  toggling,
  sampleAlertSending,
  onSave,
  onConfirmDelete,
  onCancelDelete,
  onDelete,
  onTest,
  onSendSampleAlert,
  onToggleEnabled,
}: SlackCardProps): JSX.Element {
  const [urlInput, setUrlInput] = useState('');
  const trimmedUrl = urlInput.trim();
  // Minimal client-side URL hint — server enforces the full rule.
  const urlValid =
    trimmedUrl.startsWith('https://hooks.slack.com/') && trimmedUrl.length > 30;

  const isLoading = status === undefined;
  const isConfigured = status !== null && status !== undefined && status.configured;
  const configuredStatus = isConfigured
    ? (status as Extract<SlackWebhookStatus, { configured: true }>)
    : null;
  const notifEnabled = configuredStatus?.enabled !== false;

  return (
    <Section C={C} title="Slack" icon={Send} anchorId="slack">
      {isLoading ? (
        <StatusRow C={C} pill={<Pill tone="neutral">LOADING</Pill>} />
      ) : isConfigured ? (
        <>
          <StatusRow C={C} pill={<Pill tone="mint" icon={Check}>CONNECTED</Pill>} />
          {status !== null && status !== undefined && status.configured && (
            <DetailGrid C={C}>
              <Detail C={C} label="Connected">
                <RelativeTime
                  iso={(status as Extract<SlackWebhookStatus, { configured: true }>).created_at}
                  style={{ fontFamily: sansFont, fontSize: 12 }}
                />
              </Detail>
              {(status as Extract<SlackWebhookStatus, { configured: true }>).last_used_at !== null ? (
                <Detail C={C} label="Last used">
                  <RelativeTime
                    iso={(status as Extract<SlackWebhookStatus, { configured: true }>).last_used_at as string}
                    style={{ fontFamily: sansFont, fontSize: 12 }}
                  />
                </Detail>
              ) : null}
            </DetailGrid>
          )}
          {configuredStatus !== null && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: 4,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: notifEnabled ? C.lime : C.amber,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}
                >
                  {notifEnabled
                    ? 'Sending alerts'
                    : 'Notifications paused'}
                </span>
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => onToggleEnabled(!notifEnabled)}
                  disabled={toggling}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                    color: C.text,
                    fontFamily: sansFont,
                    fontSize: 11,
                    fontWeight: 500,
                    borderRadius: 2,
                    padding: '2px 8px',
                    opacity: toggling ? 0.5 : 1,
                    cursor: toggling ? 'default' : 'pointer',
                  }}
                >
                  {toggling ? '…' : notifEnabled ? 'Pause' : 'Resume'}
                </button>
              )}
            </div>
          )}
          {canEdit ? (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={onTest}
                disabled={testing}
                className="flex items-center gap-2 px-3 py-2"
                style={{
                  background: 'transparent',
                  border: `1px solid ${C.border}`,
                  color: C.text,
                  fontFamily: sansFont,
                  fontSize: 12,
                  fontWeight: 500,
                  borderRadius: 2,
                  opacity: testing ? 0.6 : 1,
                }}
              >
                {testing ? (
                  <>
                    <RefreshCw size={12} style={{ animation: 'spin 1.4s linear infinite' }} />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send size={12} />
                    Send test message
                  </>
                )}
              </button>
              {/* Coordinator 2026-10-20 follow-up B — a second button
                  posts the real Block-Kit got-through layout with
                  fictional data so the merchant can preview the exact
                  layout they'll see on a live event. Owner/admin only,
                  shares the /test rate-limit bucket. */}
              <button
                type="button"
                data-testid="slack-send-sample-security-alert"
                onClick={onSendSampleAlert}
                disabled={sampleAlertSending}
                className="flex items-center gap-2 px-3 py-2"
                style={{
                  background: 'transparent',
                  border: `1px solid ${C.border}`,
                  color: C.text,
                  fontFamily: sansFont,
                  fontSize: 12,
                  fontWeight: 500,
                  borderRadius: 2,
                  opacity: sampleAlertSending ? 0.6 : 1,
                }}
              >
                {sampleAlertSending ? (
                  <>
                    <RefreshCw size={12} style={{ animation: 'spin 1.4s linear infinite' }} />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send size={12} />
                    Send sample security alert
                  </>
                )}
              </button>
              <DisconnectArea
                C={C}
                confirming={confirming}
                disconnecting={deleting}
                onConfirm={onConfirmDelete}
                onCancel={onCancelDelete}
                onDisconnect={onDelete}
                confirmTitle="Disconnect Slack"
                confirmBody={
                  <>
                    Disconnecting removes the webhook URL from the credential
                    vault. Alerts will stop immediately.{' '}
                    <strong>
                      You can reconnect at any time by pasting a new webhook URL.
                    </strong>
                  </>
                }
              />
            </div>
          ) : (
            <ReadOnlyHint C={C} />
          )}
        </>
      ) : (
        <>
          <StatusRow
            C={C}
            pill={<Pill tone="neutral">NOT CONNECTED</Pill>}
            body="Get Sill alerts in Slack, such as orders waiting for your review. Paste your Slack incoming-webhook URL below."
          />
          {canEdit ? (
            <div className="space-y-3">
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 1,
                  fontWeight: 600,
                }}
              >
                WEBHOOK URL
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.currentTarget.value)}
                  placeholder="https://hooks.slack.com/services/…"
                  autoComplete="off"
                  spellCheck={false}
                  className="flex-1"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 12,
                    background: C.surface2,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    color: C.text,
                    padding: '6px 10px',
                    minWidth: 260,
                    outline: 'none',
                  }}
                />
                <button
                  type="button"
                  onClick={() => onSave(urlInput)}
                  disabled={!urlValid || saving}
                  className="flex items-center gap-2 px-4 py-2"
                  style={{
                    background: !urlValid || saving ? C.surface2 : C.lime,
                    color: !urlValid || saving ? C.muted : C.ink,
                    fontFamily: sansFont,
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: 2,
                    pointerEvents: !urlValid || saving ? 'none' : 'auto',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {saving ? (
                    <>
                      <RefreshCw size={12} style={{ animation: 'spin 1.4s linear infinite' }} />
                      Connecting…
                    </>
                  ) : (
                    'Connect Slack'
                  )}
                </button>
              </div>
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 11,
                  color: C.muted,
                  lineHeight: 1.5,
                }}
              >
                In Slack: Apps → Incoming Webhooks → Add to Slack → copy the URL.
              </div>
            </div>
          ) : (
            <ReadOnlyHint C={C} />
          )}
        </>
      )}
    </Section>
  );
}

/**
 * ADR-0188 — server-side interception of `/.well-known/agent-card.json`.
 *
 * Three install paths, presented as two sub-sections:
 *
 *   1. Cloudflare token-paste deploy (primary). The merchant mints a
 *      scoped Cloudflare API token in their own dashboard (a "Get API
 *      token" deep-link jumps them to the exact creation screen) and
 *      pastes it into a form here. Sill validates the token, resolves
 *      the zone from the site's verified domain, and deploys a
 *      Sill-managed Worker on that zone that serves the signed agent
 *      card at the well-known path. Superseded the OAuth-redirect flow
 *      because Cloudflare's OAuth surface no longer supports the
 *      account-level `workers_scripts:edit` scope headlessly and the
 *      token model is what merchants actually use for their own
 *      automation. Status + redeploy + disconnect controls live here.
 *   2. Manual Cloudflare Worker download (fallback). For merchants who
 *      cannot or will not paste a token — they can download the Worker
 *      script and install it in their own Cloudflare dashboard.
 *   3. WordPress PHP plugin (WooCommerce / non-Cloudflare). Registers a
 *      `template_redirect` handler for the well-known path. For
 *      WordPress / WooCommerce sites that are NOT behind Cloudflare.
 *
 * Shopify stores don't need either — the Sill Shopify app proxy already
 * serves the well-known path through Shopify's App Proxy surface. The
 * footer copy points this out so a merchant on Shopify doesn't
 * accidentally install a redundant Worker.
 *
 * Connect flow: the "Deploy Worker" button POSTs the pasted token to
 * `POST /v1/sites/:site_id/integrations/cloudflare/connect`. On success
 * (200) the token is cleared from local state, a mint toast fires, and
 * the status is refetched. On 422 with `server_reason: 'token_invalid'`
 * or `'zone_not_found'` an inline coral hint renders below the input;
 * any other failure surfaces the standard coral toast. Nothing lives
 * in the URL — the flow is fully in-page.
 *
 * Download UX (manual sub-section): fetch the text via the bag, wrap
 * in a Blob, mint an object URL, click a synthetic anchor with
 * `download=<file>`, revoke the URL. Standard pattern; no external dep.
 */
export interface AgentReadinessCardProps {
  C: ThemeC;
  siteId: SiteId;
  /** ADR-0211 §5.4 — the coverage card needs the whole site (domain). */
  site: Site;
  api: SiteAPI;
  /**
   * Owner|admin role — the Cloudflare Connect / Redeploy / Disconnect
   * controls are hidden for reviewer|viewer (the server is authoritative;
   * this is defense-in-depth, mirroring every other Card on this page).
   */
  canManage: boolean;
  /** Settings → Install (2026-10-01): the install coverage card renders
   *  separately at the top of the tab and this component drops the
   *  duplicated card-in-card. */
  hideCoverageCard?: boolean;
  /** Settings → Install (2026-10-01): drop the leftover
   *  "Agent Readiness" title + wrapper that only made sense when this
   *  rendered inside the Integrations card. */
  bare?: boolean;
}

const AGENT_READY_CF_WORKER_FILENAME = 'sill-agent-ready-worker.js';
// WordPress's "Upload Plugin" surface accepts only `.zip` archives.
const AGENT_READY_WORDPRESS_FILENAME = 'sill-agent-ready.zip';

export function AgentReadinessCard({
  C,
  siteId,
  site,
  api,
  canManage,
  hideCoverageCard = false,
  bare = false,
}: AgentReadinessCardProps): JSX.Element {
  const toast = useToast();
  // ADR-0239 — the "Install update available" indicator reads
  // `Site.install_update_available` from the sites list, NOT from
  // the per-site Cloudflare integration status refetched below. A
  // successful redeploy clears the flag server-side but the dot
  // only disappears once the sites list is re-fetched; before this
  // the merchant had to hard-refresh to clear it (founder feedback
  // 2026-10-17 on vbout.com). Pulling `refresh` from SitesCtx and
  // kicking it after a redeploy keeps the dot in sync with the
  // served reality.
  const sites = useSites();
  const [downloadingCfWorker, setDownloadingCfWorker] = useState(false);
  const [downloadingWordpress, setDownloadingWordpress] = useState(false);

  // Cloudflare token-paste integration state. `undefined` while the
  // first fetch is in flight; a resolved value carries the current
  // deploy state. A fetch error collapses to the disconnected baseline
  // so the sub-section stays interactive.
  const [cfStatus, setCfStatus] = useState<
    CloudflareIntegrationStatus | null | undefined
  >(undefined);
  const [cfRedeploying, setCfRedeploying] = useState(false);
  const [cfDisconnecting, setCfDisconnecting] = useState(false);
  const [cfConfirmingDisconnect, setCfConfirmingDisconnect] = useState(false);
  // Token-paste form state. `apiToken` is the raw pasted string —
  // masked in the input, cleared from state on success (never persisted
  // to localStorage / sessionStorage). `connecting` gates the submit
  // button. `connectError` is the inline hint below the input for the
  // two bounded 422 branches — every other error surfaces as a toast.
  const [apiToken, setApiToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const loadCloudflareStatus = useCallback(async (): Promise<void> => {
    try {
      const next = await api.getCloudflareIntegration(siteId);
      setCfStatus(next);
    } catch {
      // Match the ConnectorStatus pattern: an error collapses to the
      // disconnected baseline so the view layer stays interactive.
      setCfStatus({
        connected: false,
        cf_zone_name: null,
        cf_script_name: null,
        cf_worker_deployed_at: null,
      });
    }
  }, [api, siteId]);

  useEffect(() => {
    void loadCloudflareStatus();
  }, [loadCloudflareStatus]);

  const handleConnectCloudflare = useCallback(async (): Promise<void> => {
    const trimmed = apiToken.trim();
    if (connecting || trimmed.length === 0) return;
    setConnecting(true);
    setConnectError(null);
    try {
      await api.connectCloudflare(siteId, trimmed);
      // Drop the token from state as soon as the server acks — it must
      // never survive past the successful mint.
      setApiToken('');
      toast({
        title: 'Cloudflare Worker deployed',
        body: 'Sill is now serving /.well-known/agent-card.json at your domain.',
        tone: 'lime',
      });
      await loadCloudflareStatus();
    } catch (err) {
      if (err instanceof ApiError) {
        // The two bounded 422 branches render as inline hints below the
        // input, matching the discipline the other forms on this page
        // (WooCommerce connect, Shopify shop input) use for their own
        // validation errors. Every other failure surfaces as a toast.
        if (err.server_reason === 'cloudflare_auth_failed') {
          setConnectError(
            'Token invalid — check it has the required permissions.',
          );
          return;
        }
        // Install 1.20 — the domain's {domain}/* route runs the merchant's
        // own Worker; Sill leaves it alone rather than switching it off.
        if (err.server_reason === CLOUDFLARE_ROUTE_IN_USE_REASON) {
          setConnectError(cloudflareRouteInUseMessage(err.server_body));
          return;
        }
        if (err.server_reason === 'no_zone_for_domain') {
          setConnectError(
            "This token is valid, but this domain isn't in your Cloudflare account. Make sure the domain is added to Cloudflare and try again.",
          );
          return;
        }
        toast({
          title: 'Could not connect Cloudflare',
          body: toastReasonForApiError(err),
          tone: 'coral',
        });
        return;
      }
      toast({
        title: 'Could not connect Cloudflare',
        body: 'Unknown error',
        tone: 'coral',
      });
    } finally {
      setConnecting(false);
    }
  }, [api, apiToken, connecting, loadCloudflareStatus, siteId, toast]);

  const handleRedeployCloudflare = useCallback(async (): Promise<void> => {
    if (cfRedeploying) return;
    setCfRedeploying(true);
    try {
      await api.redeployCloudflareWorker(siteId);
      toast({
        title: 'Cloudflare Worker redeployed',
        body: 'A fresh Worker is live on your zone.',
        tone: 'lime',
      });
      // Refetch the per-site CF status (local to this card) AND the
      // sites list — the latter carries `install_update_available`,
      // which drives the amber dot on SiteCard, InstallCoverageCard,
      // and the Dashboard InstallUpdateBanner. Without the second
      // refresh the dot persisted until a full page reload (founder
      // feedback 2026-10-17).
      await Promise.all([
        loadCloudflareStatus(),
        sites.refresh().catch(() => { /* best effort */ }),
      ]);
    } catch (err) {
      const reason =
        err instanceof ApiError ? toastReasonForApiError(err) : 'Unknown error';
      toast({
        title: 'Could not redeploy Cloudflare Worker',
        body: reason,
        tone: 'coral',
      });
    } finally {
      setCfRedeploying(false);
    }
  }, [api, cfRedeploying, loadCloudflareStatus, siteId, sites, toast]);

  const handleDisconnectCloudflare = useCallback(async (): Promise<void> => {
    if (cfDisconnecting) return;
    setCfDisconnecting(true);
    try {
      await api.disconnectCloudflare(siteId);
      toast({
        title: 'Cloudflare disconnected',
        body: 'Sill has revoked its Cloudflare access and torn down the Worker.',
        tone: 'lime',
      });
      setCfConfirmingDisconnect(false);
      await loadCloudflareStatus();
    } catch (err) {
      const reason =
        err instanceof ApiError ? toastReasonForApiError(err) : 'Unknown error';
      toast({
        title: 'Could not disconnect Cloudflare',
        body: reason,
        tone: 'coral',
      });
    } finally {
      setCfDisconnecting(false);
    }
  }, [api, cfDisconnecting, loadCloudflareStatus, siteId, toast]);

  const triggerBrowserDownload = useCallback(
    (contents: Blob | string, filename: string, mime: string): void => {
      // Standard blob-URL download pattern. Object URL is revoked in
      // the next frame so the browser has time to start the download
      // stream before the URL becomes invalid. The caller may pass a
      // ready-made Blob (binary payloads like the WordPress plugin ZIP)
      // or a text string (JS source for the Cloudflare Worker).
      if (typeof document === 'undefined' || typeof URL === 'undefined') {
        return;
      }
      const blob =
        contents instanceof Blob
          ? contents
          : new Blob([contents], { type: mime });
      const object_url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = object_url;
      anchor.download = filename;
      // The anchor need not be in the DOM for `.click()` to fire the
      // download, but Safari on iOS requires it for reliability.
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      // Defer revocation so we don't race the browser reading the URL.
      setTimeout(() => URL.revokeObjectURL(object_url), 0);
    },
    [],
  );

  const handleDownloadCfWorker = useCallback(async (): Promise<void> => {
    if (downloadingCfWorker) return;
    if (!(await confirmKeyRotation(api, siteId, 'worker'))) return;
    setDownloadingCfWorker(true);
    try {
      const script = await api.getAgentReadyCloudflareWorker(siteId);
      triggerBrowserDownload(
        script,
        AGENT_READY_CF_WORKER_FILENAME,
        'application/javascript',
      );
    } catch (err) {
      const reason = err instanceof ApiError
        ? toastReasonForApiError(err)
        : 'Unknown error';
      toast({
        title: 'Could not download Cloudflare Worker',
        body: reason,
        tone: 'coral',
      });
    } finally {
      setDownloadingCfWorker(false);
    }
  }, [api, downloadingCfWorker, siteId, toast, triggerBrowserDownload]);

  const handleDownloadWordpress = useCallback(async (): Promise<void> => {
    if (downloadingWordpress) return;
    if (!(await confirmKeyRotation(api, siteId, 'plugin'))) return;
    setDownloadingWordpress(true);
    try {
      const plugin = await api.getAgentReadyWordpressPlugin(siteId);
      triggerBrowserDownload(
        plugin,
        AGENT_READY_WORDPRESS_FILENAME,
        'application/zip',
      );
    } catch (err) {
      const reason = err instanceof ApiError
        ? toastReasonForApiError(err)
        : 'Unknown error';
      toast({
        title: 'Could not download WordPress plugin',
        body: reason,
        tone: 'coral',
      });
    } finally {
      setDownloadingWordpress(false);
    }
  }, [api, downloadingWordpress, siteId, toast, triggerBrowserDownload]);

  const Body = (
    <>
      {bare ? null : (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          Serve /.well-known/agent-card.json at your domain so AI scanners
          and agent runtimes discover Sill without JavaScript.
        </div>
      )}

      {hideCoverageCard ? null : (
        <div
          className="p-4 mb-2"
          style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
        >
          <InstallCoverageCard site={site} bare />
        </div>
      )}

      {/* Sub-section 1 — Cloudflare token-paste deploy (primary). */}
      <CloudflareSubSection
        C={C}
        siteId={siteId}
        api={api}
        status={cfStatus}
        canManage={canManage}
        confirming={cfConfirmingDisconnect}
        redeploying={cfRedeploying}
        disconnecting={cfDisconnecting}
        apiToken={apiToken}
        connecting={connecting}
        connectError={connectError}
        onApiTokenChange={(v) => {
          setApiToken(v);
          // Clear the inline hint the moment the merchant edits the
          // input — otherwise a stale error lingers under the field
          // while they're fixing it.
          if (connectError !== null) setConnectError(null);
        }}
        onConnect={() => void handleConnectCloudflare()}
        onRedeploy={() => void handleRedeployCloudflare()}
        onConfirmDisconnect={() => setCfConfirmingDisconnect(true)}
        onCancelDisconnect={() => setCfConfirmingDisconnect(false)}
        onDisconnect={() => void handleDisconnectCloudflare()}
      />

      {/* Divider between the two sub-sections. */}
      <div
        style={{
          height: 1,
          background: C.borderSoft,
          margin: '4px 0',
        }}
      />

      {/* Sub-section 2 — Manual install (fallback). */}
      <div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 1,
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          MANUAL INSTALL
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
            marginBottom: 12,
          }}
        >
          Download the file and install it yourself. Plugin updates then
          arrive in WordPress.
        </div>
        <div className="flex flex-wrap gap-3">
          <AgentReadinessDownloadOption
            C={C}
            label="Cloudflare Worker"
            ariaLabel="Download Cloudflare Worker"
            caption="any site behind Cloudflare"
            onClick={() => void handleDownloadCfWorker()}
            downloading={downloadingCfWorker}
            primary={false}
          />
          <AgentReadinessDownloadOption
            C={C}
            label="WordPress plugin"
            ariaLabel="Download WordPress plugin"
            caption={`WordPress or WooCommerce · version ${WORDPRESS_PLUGIN_VERSION}`}
            onClick={() => void handleDownloadWordpress()}
            downloading={downloadingWordpress}
            primary={false}
          />
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
            marginTop: 10,
          }}
        >
          On Shopify, the script tag records people and browser agents today. Running the Worker in front of a Shopify store is coming soon.
        </div>
      </div>

      {/* ADR-0231 — report-signing keys for Worker / plugin installs. */}
      {canManage ? <InstallKeysSection C={C} siteId={siteId} api={api} /> : null}
    </>
  );
  if (bare) {
    return <div className="flex flex-col gap-4">{Body}</div>;
  }
  return (
    <Section C={C} title="Agent Readiness" icon={Download}>
      {Body}
    </Section>
  );
}

interface CloudflareSubSectionProps {
  C: ThemeC;
  siteId: SiteId;
  api: SiteAPI;
  status: CloudflareIntegrationStatus | null | undefined;
  canManage: boolean;
  confirming: boolean;
  redeploying: boolean;
  disconnecting: boolean;
  apiToken: string;
  connecting: boolean;
  connectError: string | null;
  onApiTokenChange: (v: string) => void;
  onConnect: () => void;
  onRedeploy: () => void;
  onConfirmDisconnect: () => void;
  onCancelDisconnect: () => void;
  onDisconnect: () => void;
}

/** Deep-link to Cloudflare's API-token creation screen. Opens in a new
 *  tab from the "Get API token" affordance so the merchant lands
 *  directly on the create form (they can pre-populate scopes via
 *  Cloudflare's own template picker; we don't rely on a query-param
 *  contract we can't guarantee upstream). */
const CLOUDFLARE_API_TOKEN_CREATE_URL =
  'https://dash.cloudflare.com/profile/api-tokens/create';

function CloudflareSubSection({
  C,
  siteId,
  api,
  status,
  canManage,
  confirming,
  redeploying,
  disconnecting,
  apiToken,
  connecting,
  connectError,
  onApiTokenChange,
  onConnect,
  onRedeploy,
  onConfirmDisconnect,
  onCancelDisconnect,
  onDisconnect,
}: CloudflareSubSectionProps): JSX.Element {
  const loading = status === undefined;
  const connected = status !== null && status !== undefined && status.connected;

  return (
    <div id="cloudflare">
      <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
        <Cloud size={12} color={C.text} />
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 1,
            fontWeight: 600,
          }}
        >
          CLOUDFLARE WORKER
        </div>
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          lineHeight: 1.5,
          marginBottom: 12,
        }}
      >
        Paste your Cloudflare API token to auto-deploy the Worker to your
        zone. Sill handles the rest.
      </div>

      {loading ? (
        <StatusRow C={C} pill={<Pill tone="neutral">LOADING</Pill>} />
      ) : connected ? (
        <CloudflareConnectedDetails
          C={C}
          siteId={siteId}
          api={api}
          status={status as CloudflareIntegrationStatus}
          canManage={canManage}
          confirming={confirming}
          redeploying={redeploying}
          disconnecting={disconnecting}
          onRedeploy={onRedeploy}
          onConfirmDisconnect={onConfirmDisconnect}
          onCancelDisconnect={onCancelDisconnect}
          onDisconnect={onDisconnect}
        />
      ) : (
        <>
          <StatusRow C={C} pill={<Pill tone="neutral">NOT CONNECTED</Pill>} />
          {canManage ? (
            <CloudflareTokenForm
              C={C}
              apiToken={apiToken}
              connecting={connecting}
              connectError={connectError}
              onApiTokenChange={onApiTokenChange}
              onConnect={onConnect}
            />
          ) : (
            <ReadOnlyHint C={C} />
          )}
        </>
      )}
    </div>
  );
}

interface CloudflareTokenFormProps {
  C: ThemeC;
  apiToken: string;
  connecting: boolean;
  connectError: string | null;
  onApiTokenChange: (v: string) => void;
  onConnect: () => void;
}

/** Token-paste form for the Cloudflare NOT CONNECTED state.
 *
 *   ┌───────────────────────────────────────────────────────────┐
 *   │  [Get API token ↗]                                        │
 *   │  ┌─────────────────────────────────────────────────────┐  │
 *   │  │  API token  •••••••••••••••••••••••••••••••••••••   │  │
 *   │  └─────────────────────────────────────────────────────┘  │
 *   │  [Deploy Worker]                                          │
 *   │  Account → Account → Read                                 │
 *   │  Account → Workers Scripts → Edit                         │
 *   │  Zone    → Workers Routes → Edit   Zone → Zone → Read     │
 *   └───────────────────────────────────────────────────────────┘
 *
 * Submit gates: disabled when the token is empty (trim-length 0) or
 * a connect is already in flight. The inline `connectError` renders
 * below the input in coral for the two bounded 422 branches
 * (`token_invalid` / `zone_not_found`); every other failure is
 * a coral toast the parent surfaces via `toastReasonForApiError`.
 */
function CloudflareTokenForm({
  C,
  apiToken,
  connecting,
  connectError,
  onApiTokenChange,
  onConnect,
}: CloudflareTokenFormProps): JSX.Element {
  const trimmed = apiToken.trim();
  const submitDisabled = trimmed.length === 0 || connecting;
  return (
    <div className="space-y-3">
      <a
        href={CLOUDFLARE_API_TOKEN_CREATE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2"
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.text,
          textDecoration: 'none',
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          padding: '4px 10px',
          width: 'fit-content',
        }}
      >
        Create API token on Cloudflare
        <ExternalLink size={11} />
      </a>
      <div>
        <label
          htmlFor="settings-cloudflare-api-token"
          style={{
            display: 'block',
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 1,
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          API TOKEN
        </label>
        <input
          id="settings-cloudflare-api-token"
          type="password"
          value={apiToken}
          onChange={(e) => onApiTokenChange(e.currentTarget.value)}
          placeholder="Paste token here"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          aria-invalid={connectError !== null}
          aria-describedby={
            connectError !== null
              ? 'settings-cloudflare-api-token-error'
              : undefined
          }
          style={{
            width: '100%',
            padding: '8px 10px',
            background: C.surface2,
            border: `1px solid ${connectError !== null ? C.coralFg : C.border}`,
            borderRadius: 2,
            color: C.text,
            fontFamily: monoFont,
            fontSize: 12,
            outline: 'none',
          }}
        />
        {connectError !== null ? (
          <div
            id="settings-cloudflare-api-token-error"
            role="alert"
            style={{
              fontFamily: sansFont,
              fontSize: 11,
              color: C.coralFg,
              lineHeight: 1.5,
              marginTop: 6,
            }}
          >
            {connectError}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onConnect}
        disabled={submitDisabled}
        aria-label="Deploy Cloudflare Worker"
        className="inline-flex items-center gap-2 px-4 py-2"
        style={{
          background: submitDisabled ? C.surface2 : C.lime,
          color: submitDisabled ? C.muted : C.ink,
          fontFamily: sansFont,
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 2,
          border: 'none',
          cursor: submitDisabled ? 'default' : 'pointer',
          width: 'fit-content',
        }}
      >
        {connecting ? (
          <>
            <RefreshCw
              size={12}
              style={{ animation: 'spin 1.4s linear infinite' }}
            />
            Deploying…
          </>
        ) : (
          <>
            <Zap size={12} />
            Deploy Worker
          </>
        )}
      </button>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.8,
          borderTop: `1px solid ${C.border}`,
          paddingTop: 10,
        }}
      >
        <div style={{ marginBottom: 4 }}>
          In the token creator, add these three permissions:
        </div>
        {(
          [
            ['Account', 'Workers Scripts', 'Edit'],
            ['Zone', 'Workers Routes', 'Edit'],
            ['Zone', 'Zone', 'Read'],
          ] as const
        ).map(([cat, res, level]) => (
          <div key={`${cat}-${res}`} style={{ display: 'flex', gap: 4 }}>
            <span style={{ color: C.muted, minWidth: 56 }}>{cat}</span>
            <span style={{ color: C.muted }}>→</span>
            <span
              style={{ fontFamily: monoFont, fontSize: 11, color: C.text }}
            >
              {res}
            </span>
            <span style={{ color: C.muted }}>→</span>
            <span style={{ color: C.text }}>{level}</span>
          </div>
        ))}
        <div style={{ marginTop: 4 }}>
          Set Zone Resources to{' '}
          <span style={{ fontFamily: monoFont, fontSize: 11, color: C.text }}>
            All zones
          </span>{' '}
          or select your specific domain.
        </div>
      </div>
    </div>
  );
}

function CloudflareConnectedDetails({
  C,
  siteId,
  api,
  status,
  canManage,
  confirming,
  redeploying,
  disconnecting,
  onRedeploy,
  onConfirmDisconnect,
  onCancelDisconnect,
  onDisconnect,
}: {
  C: ThemeC;
  siteId: SiteId;
  api: SiteAPI;
  status: CloudflareIntegrationStatus;
  canManage: boolean;
  confirming: boolean;
  redeploying: boolean;
  disconnecting: boolean;
  onRedeploy: () => void;
  onConfirmDisconnect: () => void;
  onCancelDisconnect: () => void;
  onDisconnect: () => void;
}): JSX.Element {
  const showDetails =
    status.cf_zone_name !== null || status.cf_worker_deployed_at !== null;
  // ADR-0239 — surface "Update available" as an amber pill. The merchant
  // can click the existing Redeploy button to roll it forward (same
  // handler; no new write surface).
  const updateAvailable = status.install_update_available === true;
  return (
    <>
      <StatusRow
        C={C}
        pill={
          updateAvailable ? (
            <Pill tone="amber">UPDATE AVAILABLE</Pill>
          ) : (
            <Pill tone="lime" icon={Check}>
              WORKER ACTIVE
            </Pill>
          )
        }
      />
      {updateAvailable ? (
        <div
          data-testid="cf-install-update-available"
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.text,
            lineHeight: 1.5,
            padding: '8px 10px',
            background: C.amberDim,
            border: `1px solid rgba(255,182,39,0.3)`,
            borderRadius: 3,
          }}
        >
          A newer Worker template is live
          {status.install_latest !== undefined ? ` (${status.install_latest})` : ''}.
          Redeploy to pick it up — your Cloudflare token and settings stay the
          same.
        </div>
      ) : null}
      {showDetails ? (
        <DetailGrid C={C}>
          {status.cf_zone_name !== null ? (
            <Detail C={C} label="Zone">
              <span style={{ fontFamily: monoFont, fontSize: 12 }}>
                {status.cf_zone_name}
              </span>
            </Detail>
          ) : null}
          {status.cf_script_name !== null ? (
            <Detail C={C} label="Worker">
              <span style={{ fontFamily: monoFont, fontSize: 12 }}>
                {status.cf_script_name}
              </span>
            </Detail>
          ) : null}
          {status.cf_worker_deployed_at !== null ? (
            <Detail C={C} label="Deployed">
              <RelativeTime
                iso={status.cf_worker_deployed_at}
                style={{ fontFamily: sansFont, fontSize: 12 }}
              />
            </Detail>
          ) : null}
          {status.install_version !== undefined && status.install_version !== null ? (
            <Detail C={C} label="Version">
              <span style={{ fontFamily: monoFont, fontSize: 12 }}>
                {status.install_version}
              </span>
            </Detail>
          ) : null}
        </DetailGrid>
      ) : null}
      {canManage ? (
        confirming ? (
          <div
            className="p-3"
            style={{
              background: C.coralDim,
              border: `1px solid rgba(255,107,71,0.3)`,
              borderRadius: 3,
            }}
          >
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
                lineHeight: 1.5,
                marginBottom: 10,
              }}
            >
              Disconnecting revokes Sill's Cloudflare access and tears down
              the deployed Worker.{' '}
              <strong>
                /.well-known/agent-card.json will stop being served by Sill
                on your domain until you reconnect or install one of the
                manual options below.
              </strong>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onDisconnect}
                disabled={disconnecting}
                className="flex items-center gap-2 px-3 py-2"
                style={{
                  background: C.coral,
                  color: C.ink,
                  fontFamily: sansFont,
                  fontWeight: 600,
                  fontSize: 12,
                  borderRadius: 2,
                  border: 'none',
                  opacity: disconnecting ? 0.6 : 1,
                }}
              >
                {disconnecting ? (
                  <>
                    <RefreshCw
                      size={12}
                      style={{ animation: 'spin 1.4s linear infinite' }}
                    />
                    Disconnecting…
                  </>
                ) : (
                  'Disconnect Cloudflare'
                )}
              </button>
              <button
                type="button"
                onClick={onCancelDisconnect}
                disabled={disconnecting}
                className="px-3 py-2"
                style={{
                  background: 'transparent',
                  border: `1px solid ${C.border}`,
                  color: C.muted,
                  fontFamily: sansFont,
                  fontSize: 12,
                  borderRadius: 2,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={onRedeploy}
              disabled={redeploying}
              aria-label="Redeploy Cloudflare Worker"
              className="flex items-center gap-2 px-3 py-2"
              style={{
                background: 'transparent',
                border: `1px solid ${C.border}`,
                color: C.amberFg,
                fontFamily: sansFont,
                fontSize: 12,
                fontWeight: 500,
                borderRadius: 2,
                opacity: redeploying ? 0.6 : 1,
              }}
            >
              {redeploying ? (
                <>
                  <RefreshCw
                    size={12}
                    style={{ animation: 'spin 1.4s linear infinite' }}
                  />
                  Redeploying…
                </>
              ) : (
                <>
                  <RefreshCw size={12} />
                  Redeploy
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onConfirmDisconnect}
              aria-label="Disconnect Cloudflare"
              className="flex items-center gap-2 px-3 py-2"
              style={{
                background: 'transparent',
                border: `1px solid ${C.border}`,
                color: C.coralFg,
                fontFamily: sansFont,
                fontSize: 12,
                fontWeight: 500,
                borderRadius: 2,
              }}
            >
              <X size={12} />
              Disconnect
            </button>
          </div>
        )
      ) : (
        <ReadOnlyHint C={C} />
      )}
      {/* ADR-0239 — merchant-visible redeploy history. Hidden when no
          rows exist (a merchant who has never redeployed shouldn't see
          an empty section). */}
      <InstallUpdatesHistory C={C} siteId={siteId} api={api} />
    </>
  );
}

/**
 * ADR-0239 §Rollout — "Updates" history for an active Cloudflare install.
 *
 * Reads `GET /v1/sites/:site_id/install-updates` on mount (newest 50).
 * Collapsed by default to one line (count, latest version, when);
 * expanded, it pages through the redeploys 5 at a time. Hidden
 * entirely when the list is empty or the fetch fails: a merchant who
 * has never had a redeploy applied shouldn't see an empty "Updates"
 * section. The projection on the wire never carries `staff_id` or
 * `run_id`, so there's nothing merchant-sensitive to filter here —
 * we render what the server returned, verbatim.
 */
const INSTALL_UPDATES_PAGE_SIZE = 5;

function InstallUpdatesHistory({
  C,
  siteId,
  api,
}: {
  C: ThemeC;
  siteId: SiteId;
  api: SiteAPI;
}): JSX.Element | null {
  const [items, setItems] = useState<readonly InstallUpdateItem[] | null>(
    null,
  );
  // Collapsed by default: the header carries the count and the latest
  // update; expanding pages through the history 5 at a time.
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.listInstallUpdates(siteId);
        if (!cancelled) setItems(res.items);
      } catch {
        // Keep the view interactive on a transient failure: an empty
        // history is a strict superset of what a merchant on the old
        // CF integration state (never-redeployed) sees, so collapsing
        // is a safe default.
        if (!cancelled) setItems([]);
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [api, siteId]);

  if (items === null || items.length === 0) return null;

  const latest = items[0]!;
  const pages = Math.ceil(items.length / INSTALL_UPDATES_PAGE_SIZE);
  const page_items = items.slice(page * INSTALL_UPDATES_PAGE_SIZE, (page + 1) * INSTALL_UPDATES_PAGE_SIZE);
  const first = page * INSTALL_UPDATES_PAGE_SIZE + 1;
  const last = page * INSTALL_UPDATES_PAGE_SIZE + page_items.length;
  const pagerButton = (label: string, disabled: boolean, onClick: () => void): JSX.Element => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'transparent',
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        padding: '3px 8px',
        fontFamily: sansFont,
        fontSize: 11,
        color: disabled ? C.muted : C.text,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      data-testid="cf-install-updates-history"
      style={{
        marginTop: 4,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="cf-install-updates-toggle"
        className="flex items-center justify-between flex-wrap gap-2"
        style={{
          width: '100%',
          padding: '8px 10px',
          background: 'transparent',
          border: 'none',
          borderBottom: open ? `1px solid ${C.border}` : 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDown size={12} color={C.muted} /> : <ChevronRight size={12} color={C.muted} />}
          <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1, fontWeight: 600 }}>
            UPDATES · {items.length}
          </span>
        </span>
        <span className="flex items-center gap-2" style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
          <span>Latest{latest.target_version !== null ? ` v${latest.target_version}` : ''}</span>
          <RelativeTime iso={latest.created_at} style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }} />
        </span>
      </button>
      {open ? (
        <>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}
          >
            {page_items.map((item, i) => (
              <li
                key={`${item.created_at}-${first + i}`}
                className="flex items-center justify-between flex-wrap gap-2"
                style={{
                  padding: '8px 10px',
                  borderTop: i === 0 ? 'none' : `1px solid ${C.borderSoft}`,
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.text,
                  lineHeight: 1.5,
                }}
              >
                <div className="flex items-center gap-2">
                  <RelativeTime
                    iso={item.created_at}
                    style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}
                  />
                  {item.target_version !== null ? (
                    <span
                      style={{
                        fontFamily: monoFont,
                        fontSize: 11,
                        color: C.muted,
                      }}
                    >
                      v{item.target_version}
                    </span>
                  ) : null}
                </div>
                <div style={{ color: C.muted }}>
                  {item.triggered_by === 'merchant'
                    ? 'Updated by you or your team'
                    : 'Updated by Sill'}
                </div>
              </li>
            ))}
          </ul>
          {pages > 1 ? (
            <div
              data-testid="cf-install-updates-pager"
              className="flex items-center justify-between gap-2"
              style={{ padding: '6px 10px', borderTop: `1px solid ${C.borderSoft}` }}
            >
              <span style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}>
                {first}–{last} of {items.length}
              </span>
              <span className="flex items-center gap-2">
                {pagerButton('Newer', page === 0, () => setPage((p) => Math.max(0, p - 1)))}
                {pagerButton('Older', page >= pages - 1, () => setPage((p) => Math.min(pages - 1, p + 1)))}
              </span>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function AgentReadinessDownloadOption({
  C,
  label,
  ariaLabel,
  caption,
  onClick,
  downloading,
  primary,
}: {
  C: ThemeC;
  label: string;
  /** Optional accessible name — defaults to `label`. Lets the visible
   *  text stay short (e.g. "Cloudflare Worker") while screen readers
   *  still hear "Download Cloudflare Worker". */
  ariaLabel?: string;
  caption: string;
  onClick: () => void;
  downloading: boolean;
  primary: boolean;
}): JSX.Element {
  const disabled = downloading;
  return (
    <div className="flex flex-col gap-1" style={{ minWidth: 240 }}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel ?? label}
        className="inline-flex items-center gap-2 px-4 py-2"
        style={{
          background: primary
            ? disabled
              ? C.surface2
              : C.lime
            : 'transparent',
          border: primary ? 'none' : `1px solid ${C.border}`,
          color: primary
            ? disabled
              ? C.muted
              : C.ink
            : disabled
              ? C.muted
              : C.text,
          fontFamily: sansFont,
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 2,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled && !primary ? 0.6 : 1,
          width: 'fit-content',
        }}
      >
        {downloading ? (
          <>
            <RefreshCw
              size={12}
              style={{ animation: 'spin 1.4s linear infinite' }}
            />
            Downloading…
          </>
        ) : (
          <>
            <Download size={12} />
            {label}
          </>
        )}
      </button>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        {caption}
      </div>
    </div>
  );
}

function CustomEmbedCard({ C }: { C: ThemeC }): JSX.Element {
  return (
    <Section C={C} title="Custom embed" icon={ShoppingBag} last>
      <StatusRow
        C={C}
        pill={<Pill tone="neutral">EMBED-ONLY</Pill>}
        body="Sill's embed snippet is installed on this site directly. There is no credential to disconnect — to stop recording, remove the snippet from your site or delete this site in the Danger zone."
      />
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

function Section({
  C,
  title,
  icon: Icon,
  last,
  anchorId,
  children,
}: {
  C: ThemeC;
  title: string;
  icon: typeof ShoppingBag;
  last?: boolean;
  /** Optional stable id so Settings deep-links (`?settings_section=…`)
   *  can scroll + outline this card (founder bug 2026-10-20 Report 2). */
  anchorId?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className="py-5"
      {...(anchorId !== undefined ? { id: anchorId } : {})}
      style={{ borderBottom: last === true ? 'none' : `1px solid ${C.borderSoft}` }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} color={C.text} />
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            color: C.text,
            fontWeight: 600,
          }}
        >
          {title}
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function StatusRow({
  C,
  pill,
  body,
}: {
  C: ThemeC;
  pill: ReactNode;
  body?: string;
}): JSX.Element {
  return (
    <div className="space-y-2">
      <div>{pill}</div>
      {body !== undefined ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          {body}
        </div>
      ) : null}
    </div>
  );
}

function ConnectAction({
  C,
  href,
  label,
  disabled,
}: {
  C: ThemeC;
  href: string;
  label: string;
  disabled: boolean;
}): JSX.Element {
  return (
    <a
      href={disabled ? undefined : href}
      className="inline-flex items-center gap-2 px-4 py-2"
      style={{
        background: disabled ? C.surface2 : C.lime,
        color: disabled ? C.muted : C.ink,
        fontFamily: sansFont,
        fontSize: 13,
        fontWeight: 600,
        borderRadius: 2,
        pointerEvents: disabled ? 'none' : 'auto',
        textDecoration: 'none',
        width: 'fit-content',
      }}
    >
      {label}
    </a>
  );
}

function DetailGrid({
  C,
  children,
}: {
  C: ThemeC;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      {children}
    </div>
  );
}

function Detail({
  C,
  label,
  children,
}: {
  C: ThemeC;
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 1,
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        {label.toUpperCase()}
      </div>
      <div style={{ color: C.text }}>{children}</div>
    </div>
  );
}

function DisconnectArea({
  C,
  confirming,
  disconnecting,
  onConfirm,
  onCancel,
  onDisconnect,
  confirmTitle,
  confirmBody,
}: {
  C: ThemeC;
  confirming: boolean;
  disconnecting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onDisconnect: () => void;
  confirmTitle: string;
  confirmBody: ReactNode;
}): JSX.Element {
  if (confirming) {
    return (
      <div
        className="p-3"
        style={{
          background: C.coralDim,
          border: `1px solid rgba(255,107,71,0.3)`,
          borderRadius: 3,
        }}
      >
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.text,
            lineHeight: 1.5,
            marginBottom: 10,
          }}
        >
          {confirmBody}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onDisconnect}
            disabled={disconnecting}
            className="flex items-center gap-2 px-3 py-2"
            style={{
              background: C.coralFg,
              color: C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 12,
              borderRadius: 2,
              opacity: disconnecting ? 0.6 : 1,
            }}
          >
            {disconnecting ? (
              <>
                <RefreshCw
                  size={12}
                  style={{ animation: 'spin 1.4s linear infinite' }}
                />
                Disconnecting…
              </>
            ) : (
              confirmTitle
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={disconnecting}
            className="px-3 py-2"
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.muted,
              fontFamily: sansFont,
              fontSize: 12,
              borderRadius: 2,
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onConfirm}
      className="flex items-center gap-2 px-3 py-2"
      style={{
        background: 'transparent',
        border: `1px solid ${C.border}`,
        color: C.coralFg,
        fontFamily: sansFont,
        fontSize: 12,
        fontWeight: 500,
        borderRadius: 2,
        width: 'fit-content',
      }}
    >
      <X size={12} />
      Disconnect
    </button>
  );
}

function ReadOnlyHint({ C }: { C: ThemeC }): JSX.Element {
  return (
    <div
      style={{
        fontFamily: sansFont,
        fontSize: 11,
        color: C.muted,
        lineHeight: 1.5,
      }}
    >
      Your role can view this connection but cannot disconnect it. Ask an
      owner or admin.
    </div>
  );
}

function ReadOnlyConnectHint({ C }: { C: ThemeC }): JSX.Element {
  return (
    <div
      style={{
        fontFamily: sansFont,
        fontSize: 11,
        color: C.muted,
        lineHeight: 1.5,
      }}
    >
      Your role can view connections but cannot start a new one. Ask an
      owner or admin.
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type CardState = 'loading' | 'not_connected' | 'connected' | 'error';

/** Map a `ConnectorStatus` into the Card's display state. `undefined`
 *  → still fetching; `null` → the fetch errored out (we collapse to
 *  not_connected so the Card stays clickable). */
function readState(
  status: ConnectorStatus | null | undefined,
  expected_kind: ConnectorStatusKindFilter,
): CardState {
  if (status === undefined) return 'loading';
  if (status === null) return 'not_connected';
  if (status.kind === null || status.status === null) return 'not_connected';
  if (status.kind !== expected_kind) return 'not_connected';
  if (status.status === 'connected') return 'connected';
  if (status.status === 'error') return 'error';
  return 'not_connected';
}

function formatShopifyEmbedLabel(
  s: ConnectorStatus['embed_install_status'],
): string | null {
  switch (s) {
    case 'installed':
      return 'Installed';
    case 'scope_denied':
      return 'Reconnect to install';
    case 'pending_retry':
      return 'Retry install';
    case 'pending':
      return 'Reconnect to install';
    case 'not_applicable':
      return null;
    case undefined:
      return null;
  }
}

function disconnectTitleFor(kind: ConnectorStatusKindFilter): string {
  switch (kind) {
    case 'shopify':
      return 'Shopify disconnected';
    case 'stripe':
      return 'Stripe disconnected';
    case 'woocommerce':
      return 'WooCommerce disconnected';
    case 'custom_embed':
      return 'Custom embed disconnected';
  }
}

function disconnectBodyFor(kind: ConnectorStatusKindFilter): string {
  switch (kind) {
    case 'shopify':
      return 'To fully remove the embed, uninstall the Sill app in your Shopify admin.';
    case 'stripe':
      return 'The payment rail has also been disabled. Reconnect Stripe to re-enable it.';
    case 'woocommerce':
      return 'To fully rescind access, delete the Sill REST API key from your WooCommerce store.';
    case 'custom_embed':
      return 'Remove the embed snippet from your site to stop recording agent visits.';
  }
}

function disconnectErrorTitleFor(kind: ConnectorStatusKindFilter): string {
  switch (kind) {
    case 'shopify':
      return 'Could not disconnect Shopify';
    case 'stripe':
      return 'Could not disconnect Stripe';
    case 'woocommerce':
      return 'Could not disconnect WooCommerce';
    case 'custom_embed':
      return 'Could not disconnect custom embed';
  }
}

function toastReasonForApiError(err: ApiError): string {
  // ADR-0263 S4 — plan_feature_required 403 (e.g. slack_held_alerts).
  const details = planFeatureRequiredDetails(err);
  if (details !== null) return details.message;
  switch (err.kind) {
    case 'forbidden':
      return 'Your role cannot disconnect this integration.';
    case 'not_found':
      return 'This site is no longer accessible.';
    case 'bad_request':
      return 'The disconnect request was rejected.';
    case 'rate_limited':
      return 'Too many requests. Try again in a moment.';
    case 'service_unavailable':
      return 'The disconnect surface is unavailable on this deploy.';
    // Founder bug 2026-10-20 Report 3 — Pausing Slack from SlackCard
    // surfaced the bare error kind ("network_error") in the toast body
    // because the default branch returned `err.message`, which for a
    // kind-only ApiError is literally the kind string. Give the
    // network-timeout / preflight-failure case its own copy so the
    // merchant has something actionable to try.
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return "Couldn't reach Sill from this network. Check your connection and try again.";
    default:
      return err.message.length > 0 ? err.message : 'Unknown error.';
  }
}

